import React, { useCallback, useContext, useMemo } from "react";

import cx from "classnames";

import ActionItemCompletionMenu, { type AssigneeCompletionData } from "~/actionItems/ActionItemCompletionMenu";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import { toggleNoteBlockStatus } from "~/actionItems/utils";
import Checkbox from "~/common/NoteCheckbox";
import { useAiActionItemsUserSettingAndAcceptFlowIsOn } from "~/meetingRecorder/utils";
import Tooltip from "~/ui/Tooltip";

import { NoteBlockStatus, StatusCondition } from "./enum";

import styles from "./ActionItemCheckbox.module.css";

interface ActionItemCheckboxProps {
	id: string;
	done: boolean;
	wontDo: boolean;
	hasOriginalBlock: boolean;
	statusCondition: StatusCondition | null;
	assignees: Array<AssigneeCompletionData>;
	inMultiDragging?: boolean;
	isDisabled?: boolean;
	aiGenerated?: boolean;
}

export function AcceptAndCompleteTooltip({ children }: { children: React.ReactNode }) {
	return (
		<Tooltip content="Accept & Complete" placement="top">
			{/* We need to wrap the children in a <span> here or the tooltip won't render  */}
			<span>{children}</span>
		</Tooltip>
	);
}

export default function ActionItemCheckBox({
	id,
	done,
	wontDo,
	hasOriginalBlock,
	statusCondition,
	assignees,
	inMultiDragging,
	isDisabled,
	aiGenerated = false,
}: ActionItemCheckboxProps) {
	const { onMarkItemsAsCompleted, onMarkItemsAsIncompleted, onUpdateItemsAttrs, inPanel, renderingContext, viewer } =
		useContext(ActionItemsContext);
	const user = viewer?.user;

	const inSeriesPanel = renderingContext === "seriesPanel";

	const useCompletionMenu = useMemo(
		() =>
			statusCondition === StatusCondition.ALL &&
			assignees.length > 0 &&
			!assignees.find(assignee => assignee.id === user?.id),
		[statusCondition, assignees, user],
	);
	const showAssignYourself = useMemo(
		() => !Boolean(assignees.find(assignee => assignee.id === user?.id)),
		[user, assignees],
	);

	const state = useMemo(() => {
		const blockState = wontDo ? "indeterminate" : done ? "checked" : "unchecked";
		if (statusCondition === StatusCondition.SINGLE) {
			return blockState;
		}
		// If completed by all we first check if the user is an assignee
		// In that case we show the state for that user and not the one in the block
		const userAssignment = assignees.find(assignee => assignee.id === user?.id);
		if (!userAssignment) {
			return blockState;
		}
		return userAssignment.status === NoteBlockStatus.WONT_DO
			? "indeterminate"
			: userAssignment.status === NoteBlockStatus.DONE
				? "checked"
				: "unchecked";
	}, [done, wontDo, statusCondition, assignees, user]);

	const acceptAIItemsFFOn = useAiActionItemsUserSettingAndAcceptFlowIsOn();
	const showAcceptAndCompleteTooltip = acceptAIItemsFFOn && aiGenerated && state !== "checked";

	const onClick = useCallback(() => {
		// If it is completed by all we check if the user is an assignee
		// If so we toggle the status for that assignee
		// If not we open the assignee completion menu
		if (statusCondition === StatusCondition.ALL) {
			if (useCompletionMenu) {
				// Should open completion menu
				return;
			}
			const newAssignees =
				assignees?.map(assignee => {
					if (assignee.id !== user?.id) {
						return {
							id: assignee.id,
							status: assignee.status,
						};
					}
					return {
						id: assignee.id,
						status: assignee.status === NoteBlockStatus.NOT_DONE ? NoteBlockStatus.DONE : NoteBlockStatus.NOT_DONE,
					};
				}) || [];

			const attrsToUpdate: { assignees: AssigneeCompletionData[]; aiGenerated?: boolean } = { assignees: newAssignees };
			if (showAcceptAndCompleteTooltip) {
				attrsToUpdate.aiGenerated = false;
			}
			onUpdateItemsAttrs([id], attrsToUpdate);
			return;
		}
		// If it is completed by any
		if (wontDo || done) {
			onMarkItemsAsIncompleted([id]);
			return;
		}

		onMarkItemsAsCompleted([id]);
	}, [
		statusCondition,
		wontDo,
		done,
		onMarkItemsAsCompleted,
		id,
		useCompletionMenu,
		assignees,
		onUpdateItemsAttrs,
		user?.id,
		onMarkItemsAsIncompleted,
		showAcceptAndCompleteTooltip,
	]);

	const onToggleForAssignee = useCallback(
		(assignee: AssigneeCompletionData) => {
			if (!assignees) return;
			const newAssignees = assignees.map(currentAssignee => {
				return {
					id: currentAssignee.id,
					status:
						currentAssignee.id !== assignee.id
							? currentAssignee.status
							: toggleNoteBlockStatus(currentAssignee.status as NoteBlockStatus),
				};
			});
			const attrsToUpdate: { assignees: AssigneeCompletionData[]; aiGenerated?: boolean } = { assignees: newAssignees };

			if (showAcceptAndCompleteTooltip) {
				attrsToUpdate.aiGenerated = false;
			}
			onUpdateItemsAttrs([id], attrsToUpdate);
		},
		[assignees, showAcceptAndCompleteTooltip, onUpdateItemsAttrs, id],
	);

	const onAssignYourself = useCallback(() => {
		if (!user) return;
		const newAssignees = [
			...assignees,
			{
				id: user.id,
				status: NoteBlockStatus.NOT_DONE,
			},
		];
		onUpdateItemsAttrs([id], { assignees: newAssignees, done: false, wontDo: false });
	}, [user, id, assignees, onUpdateItemsAttrs]);

	const checkbox = useMemo(
		() => (
			<Checkbox
				type="action-item"
				linked={hasOriginalBlock}
				className={cx(styles.checkbox, {
					[styles.inMultiDragging]: inMultiDragging,
				})}
				state={state}
				onChange={onClick}
				disabled={isDisabled}
				aiGenerated={aiGenerated}
				checkboxSize={inSeriesPanel ? "small" : inPanel ? "medium" : "large"}
			/>
		),
		[hasOriginalBlock, inMultiDragging, inPanel, inSeriesPanel, state, onClick, isDisabled, aiGenerated],
	);

	const checkboxWithTooltip = useMemo(() => {
		// Only show tooltip for AI Suggestions that are not yet done
		if (showAcceptAndCompleteTooltip) {
			return <AcceptAndCompleteTooltip>{checkbox}</AcceptAndCompleteTooltip>;
		}
		return checkbox;
	}, [showAcceptAndCompleteTooltip, checkbox]);

	return useCompletionMenu ? (
		<ActionItemCompletionMenu
			trigger={<button className={styles.checkboxButton}>{checkboxWithTooltip}</button>}
			assignees={assignees}
			showAssignYourself={showAssignYourself}
			onToggle={onToggleForAssignee}
			onAssignYourself={onAssignYourself}
		/>
	) : (
		checkboxWithTooltip
	);
}
